/**
 * チェックボックスをスイッチ風に見せる共通トグル (sr-only input + track + thumb)。
 *
 * design-and-code レビュー指摘 (2026-09-03、platform-admin cross-tenant feature-flag 実装時):
 * 同一のマークアップが `FeaturesSettingsPage.tsx` / `NotificationsSettingsPage.tsx` /
 * `ProfilePage.tsx` / `PlatformAdminPage.tsx` の4箇所に複製されており、CLAUDE.md §6 が定める
 * 「4箇所目で抽出する」閾値を超えていたため、ここへ集約した。
 *
 * ⚠️ 各画面固有の disabled 判定 (`canEdit`・`is_implemented`・`toggleMut.isPending` 等) は
 * このコンポーネントに持ち込まない — 呼び出し側が意味を理解した上で `disabled` prop を渡す。
 * ⚠️ タップ領域: `min-h-[44px] min-w-[44px] justify-center` を既定にした。従来この3クラスを
 * 揃えて持っていたのは `PlatformAdminPage.tsx` のみで、`NotificationsSettingsPage.tsx`/
 * `ProfilePage.tsx` は `min-h-[44px]` のみ (幅の保証が無かった)、`FeaturesSettingsPage.tsx` に
 * 至っては高さ・幅とも指定が無く `shrink-0` のみだった (トラック自体の実測幅は40pxで44px未達)。
 * 挙動の変更は無く、ラベル自身の幅がわずかに広がりヒット領域が改善するだけのため合わせて統一する
 * (design-and-codeレビューで実測・確認済み: 4画面とも `justify-between` 行のラベル側は `shrink-0`
 * 固定で元々フレックス縮小に参加しておらず、対向側は `min-w-0` かラップ可能なテキストのため
 * 押し出しは発生しない)。
 * ⚠️ 色トークン: `bg-surface-line` に統一 (旧 `bg-neutral-line` は `#EFECE8` の同一値エイリアス、
 * tailwind.config.js 参照。見た目の変化は無い)。
 */
export function ToggleSwitch({
  checked, disabled, onChange, ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  /** 可視ラベルは呼び出し側が別途描画する。ここでは a11y 用の aria-label のみを受け取る。 */
  ariaLabel: string;
}) {
  return (
    <label className="relative inline-flex items-center cursor-pointer shrink-0 min-h-[44px] min-w-[44px] justify-center">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
        aria-label={ariaLabel}
      />
      <div className="w-10 h-6 bg-surface-line rounded-full peer-checked:bg-brand-red transition disabled:opacity-50 peer-disabled:opacity-50" />
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition peer-checked:translate-x-4" />
    </label>
  );
}
